import React, { useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Truck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Accounts } from "../lib/api";
import { cn } from "../lib/utils";

/** Yes/No toggle styled radio. value = true | false | null. */
function YesNoToggle({ value, onChange, testid }) {
  const opt = (val, label) => (
    <button
      type="button"
      onClick={() => onChange(val)}
      data-testid={`${testid}-${val ? "yes" : "no"}`}
      className={cn(
        "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-all",
        value === val
          ? val
            ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-200"
            : "border-rose-500 bg-rose-50 text-rose-800 ring-2 ring-rose-200"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-2" data-testid={testid}>
      {opt(true, "Yes")}
      {opt(false, "No")}
    </div>
  );
}

/** Single shared "Release for Further Processing" button + dialog.
 *  Used on Accounts Workspace, Sales Detail Page, and Transit list views.
 */
export function ReleaseTransitButton({
  saleId,
  userId,
  vinPresent,
  onDone,
  variant = "default",
}) {
  const [open, setOpen] = useState(false);
  const [arrived, setArrived] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const onRelease = async () => {
    if (arrived !== true) {
      toast.error("Confirm vehicle has arrived to release the hold");
      return;
    }
    if (!vinPresent) {
      toast.error("VIN must be available before releasing transit hold");
      return;
    }
    setBusy(true);
    try {
      await Accounts.releaseTransit(saleId, {
        arrived: true, notes: notes || null, user_id: userId,
      });
      toast.success("Transit Hold released · Workflow resumed", { duration: 3000 });
      setOpen(false);
      setArrived(null);
      setNotes("");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to release");
    } finally { setBusy(false); }
  };

  const buttonClasses = variant === "compact"
    ? "gap-1.5 bg-blue-600 hover:bg-blue-700"
    : "gap-1.5 bg-blue-600 hover:bg-blue-700 px-4";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={buttonClasses} data-testid="release-transit-btn">
          <Truck className="h-4 w-4" /> Release for Further Processing
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-blue-600" />
            Release for Further Processing
          </DialogTitle>
          <DialogDescription>
            Confirm vehicle has arrived and release workflow for further processing.
            Releasing activates Back Office tasks (Insurance, VAS · PP / AMC / RSA) and
            resumes SLA timers. VIN must be on file.
          </DialogDescription>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          This action cannot be undone — the audit log will record release time and user.
        </p>
        {!vinPresent && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
            VIN is missing on this sale. Capture VIN via PDI workspace first.
          </div>
        )}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Vehicle arrived at location? *</Label>
            <YesNoToggle value={arrived} onChange={setArrived} testid="toggle-arrived" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="release-notes"
              placeholder="e.g. arrived 10am, parked at bay 2"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="release-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onRelease}
            disabled={busy || !vinPresent || arrived !== true}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="confirm-release-transit"
          >
            {busy ? "Releasing…" : "Confirm Release"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
